import {
  CountryCode,
  formatIncompletePhoneNumber,
} from 'libphonenumber-js/min';

/**
 * Format the input as a phone number, maintaining selection position
 * and trimming out any spaces that are adjacent to the cursor
 * to ensure the cursor cannot get stuck
 *
 * Also prevents inputting leading zeroes, converting them to a + character
 */
export function formatInputField(
  input: HTMLInputElement,
  countryHint?: CountryCode | undefined,
) {
  // 1: Get current and desired value
  const baseValue = input.value;
  // 2: Calculate masked indexes for selection
  function preprocess(s: string) {
    return s.replace(/^[+0]+/, '+').replace(/[^+\d]/g, '');
  }
  const selectionDigits = {
    start: preprocess(baseValue.slice(0, input.selectionStart ?? 0)).length,
    end: preprocess(baseValue.slice(0, input.selectionEnd ?? 0)).length,
    direction: input.selectionDirection,
  };

  // 3: Format
  const formattedValue = formatIncompletePhoneNumber(
    preprocess(baseValue),
    countryHint,
  );

  let outValue;
  // 4: (usability) Remove spaces immediately adjacent to cursor
  // && calculate selection positions in new value
  const selection = {
    start: selectionDigits.start === 0 ? 0 : -1,
    end: selectionDigits.start === 0 ? 0 : -1,
  };
  outValue = '';
  for (let i = 0; i < formattedValue.length; i++) {
    const c = formattedValue.charAt(i);
    if (/\d/.test(c) || (c === '+' && i === 0)) {
      // Count characters & update selection
      outValue += c;
      selectionDigits.start -= 1;
      selectionDigits.end -= 1;
      if (selectionDigits.start === 0) selection.start = outValue.length;
      if (selectionDigits.end === 0) selection.end = outValue.length;
    } else {
      // Ignore all other formatting characters
    }
  }
  // 5: Commit new value & selection
  // eslint-disable-next-line no-param-reassign
  input.value = outValue;
  input.setSelectionRange(
    selection.start,
    selection.end,
    selectionDigits.direction ?? 'none',
  );
}

/**
 * Given a string such as 'Us', convert it to regional indicators '🇺🇸' (🇺+🇸)
 */
export function toRegionalIndicators(text: string) {
  let out = '';
  const normText = text.toLowerCase();

  /** Start of lowercase alphabet */
  const a = 'a'.codePointAt(0)!;
  /** Start of regional indicator alphabet */
  const regA = '🇦'.codePointAt(0)!;

  for (let i = 0; i < normText.length; i++) {
    // Index in regular alphabet
    const letterIndex = normText.codePointAt(i)! - a;
    // take the same index in the regional indicator alphabet to get the regional indicator
    out += String.fromCodePoint(regA + letterIndex);
  }
  return out;
}
